import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { updatePrivacySettings } from '../../services/privacyService';
import { Switch } from '@headlessui/react';
import { ShieldCheckIcon, LockClosedIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface PrivacySettingsProps {
  userId: string;
}

const PrivacySettings: React.FC<PrivacySettingsProps> = ({ userId }) => {
  const { t } = useTranslation();
  const [isPrivate, setIsPrivate] = useState(false);
  const [allowDirectMessages, setAllowDirectMessages] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPrivacySettings = async () => {
      try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsPrivate(userData.isPrivate || false);
          setAllowDirectMessages(userData.allowDirectMessages !== false); // Por defecto true
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar configuración de privacidad:', error);
        toast.error(t('settings.errorLoadingPrivacy'));
        setLoading(false);
      }
    };

    fetchPrivacySettings();
  }, [userId, t]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await updatePrivacySettings(userId, isPrivate, allowDirectMessages);
      toast.success(t('settings.privacySettingsSaved'));
      setSaving(false);
    } catch (error) {
      console.error('Error al guardar configuración de privacidad:', error);
      toast.error(t('settings.errorSavingPrivacy'));
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-domired-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
      <div className="flex items-center mb-4">
        <ShieldCheckIcon className="h-6 w-6 text-domired-600 dark:text-domired-500 mr-2" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('settings.privacySettings')}
        </h2>
      </div>
      
      <div className="space-y-6">
        {/* Cuenta privada */}
        <div className="flex items-center justify-between">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <LockClosedIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                {t('settings.privateAccount')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settings.privateAccountDescription')}
              </p>
            </div>
          </div>
          <Switch
            checked={isPrivate}
            onChange={setIsPrivate}
            className={`${
              isPrivate ? 'bg-domired-600' : 'bg-gray-300 dark:bg-gray-600'
            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2`}
          >
            <span
              className={`${
                isPrivate ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>
        
        {/* Mensajes directos */}
        <div className="flex items-center justify-between">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <ChatBubbleLeftIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">
                {t('settings.allowDirectMessages')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settings.allowDirectMessagesDescription')}
              </p>
            </div>
          </div>
          <Switch
            checked={allowDirectMessages}
            onChange={setAllowDirectMessages}
            className={`${
              allowDirectMessages ? 'bg-domired-600' : 'bg-gray-300 dark:bg-gray-600'
            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2`}
          >
            <span
              className={`${
                allowDirectMessages ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>
        
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-domired-600 hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {t('common.saving')}
              </>
            ) : (
              t('common.save')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;
